/**
 * Financial Reports System - Type Definitions
 * Phase 1.2: Comprehensive type definitions for all report categories
 *
 * Defines types for the comprehensive financial reporting system including:
 * - Report categories (categories, cost centers, payables, receivables, cash flow)
 * - Date basis options (competence, cash, due date)
 * - Report periods (daily, weekly, monthly, quarterly, yearly)
 * - Filter interfaces and data structures
 * - Detailed report data types for 40 report types across 5 categories
 */

import type { Transaction } from './transaction';
import type { Category } from './category';
import type { CostCenter } from './cost-center';
import type { PaymentAccount } from './payment-account';

// ============================================================================
// ENUMS - Core Report Configuration
// ============================================================================

/**
 * Main report categories available in the system
 */
export enum ReportCategory {
  CATEGORIES = 'categories',
  COST_CENTERS = 'cost-centers',
  PAYABLES = 'payables',
  RECEIVABLES = 'receivables',
  CASH_FLOW = 'cash-flow'
}

/**
 * Date basis for transaction reporting
 * - COMPETENCE: Based on competence_date (when transaction occurred)
 * - CASH: Based on payment_date (when transaction was paid)
 * - DUE: Based on due_date (when transaction is due)
 */
export enum DateBasis {
  COMPETENCE = 'competence',
  CASH = 'cash',
  DUE = 'due'
}

/**
 * Report period aggregation options
 */
export enum ReportPeriod {
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
  QUARTERLY = 'quarterly',
  YEARLY = 'yearly'
}

/**
 * Report export format options
 */
export enum ReportFormat {
  PDF = 'pdf',
  XLSX = 'xlsx',
  CSV = 'csv',
  JSON = 'json'
}

/**
 * Report sort order
 */
export enum SortOrder {
  ASC = 'asc',
  DESC = 'desc'
}

// Legacy type aliases for backward compatibility
export type ReportCategoryLegacy =
  | 'categories'
  | 'cost-centers'
  | 'payables'
  | 'receivables'
  | 'cash-flow';

export type DateBasisLegacy = 'competence' | 'cash' | 'due';

export type ReportPeriodLegacy = 'daily' | 'weekly' | 'monthly' | 'yearly';

// ============================================================================
// PERIOD FORMATTING TYPES
// ============================================================================

/**
 * Period type for dynamic formatting system
 * Defines supported temporal aggregation periods
 */
export type PeriodType = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';

/**
 * Configuration for period-specific formatting
 * Provides all necessary metadata for dynamic adaptation
 */
export interface PeriodConfig {
  /** Display name (e.g., "Diária", "Mensal") */
  displayName: string;

  /** Short name for labels (e.g., "Dia", "Mês") */
  shortName: string;

  /** date-fns format string for dates */
  dateFormat: string;

  /** Backend aggregation key */
  aggregationKey: string;

  /** X-axis label for charts */
  xAxisLabel: string;
}

export type ReportType =
  // Category Reports (8)
  | 'monthly-competence-by-category'
  | 'top-category-competence'
  | 'monthly-cash-by-category'
  | 'top-expenses-by-category'
  | 'expense-distribution'
  | 'revenue-distribution'
  | 'expense-category-status'
  | 'revenue-category-status'
  // Cost Center Reports (5)
  | 'cost-center-monthly-payments'
  | 'monthly-balance-by-cost-center'
  | 'cost-center-status'
  | 'cost-center-category-breakdown'
  | 'realized-cash-flow-by-cost-center'
  // Payables Reports (10)
  | 'supplier-payments-analysis'
  | 'supplier-analysis'
  | 'top-suppliers-by-expense'
  | 'average-payment-period'
  | 'payables-list'
  | 'paid-payables-list'
  | 'purchase-financial-status'
  | 'supplier-financial-status'
  | 'payables-financial-status'
  | 'overdue-payables-analysis'
  // Receivables Reports (14)
  | 'default-analysis'
  | 'payment-method-distribution'
  | 'overdue-bucket-analysis'
  | 'top-clients-by-revenue'
  | 'average-receipt-period'
  | 'client-balance-by-competence'
  | 'due-receivables-list'
  | 'open-receivables-list'
  | 'received-revenues-list'
  | 'client-receipts-by-bank'
  | 'client-financial-status'
  | 'receivables-financial-status'
  | 'client-default-risk'
  | 'receivables-aging-report'
  // Cash Flow Reports (12)
  | 'bank-balances-by-month'
  | 'daily-balance-analysis'
  | 'weekly-due-dates'
  | 'average-payment-receipt-periods'
  | 'period-comparison-chart'
  | 'transaction-statement'
  | 'monthly-cash-flow'
  | 'revenue-expense-by-due-date'
  | 'bank-balance-chart'
  | 'revenue-expense-status'
  | 'daily-bank-balances'
  | 'monthly-bank-balances'
  | 'projected-cash-flow';

export interface ReportFilters {
  // Primary filters (all reports)
  dateRange: { from: Date; to: Date };
  dateBasis?: DateBasis;
  period?: ReportPeriod;

  // Category filters
  categoryId?: number;
  categoryIds?: number[];
  transactionType?: 'income' | 'expense';

  // Cost center filters
  costCenterId?: number;
  costCenterIds?: number[];
  includeBudgetComparison?: boolean;

  // Client/Supplier filters
  clientId?: string;
  clientIds?: string[];
  supplierId?: string;
  supplierIds?: string[];

  // Payment/Account filters
  paymentAccountId?: number;
  paymentAccountIds?: number[];
  paymentMethod?: string;

  // Status filters
  isPaid?: boolean;
  isOverdue?: boolean;
  status?: 'active' | 'inactive' | 'all';
}

export interface ReportData<T = any> {
  data: T[];
  summary: ReportSummary;
  metadata: ReportMetadata;
}

export interface ReportSummary {
  totalIncome: number;
  totalExpense: number;
  balance: number;
  transactionCount: number;
  period: string;
  averageIncome?: number;
  averageExpense?: number;
}

export interface ReportMetadata {
  generatedAt: Date;
  filters: ReportFilters;
  dataPoints: number;
  reportType: ReportType;
  reportCategory: ReportCategory;
  userId: string;
}

// Specific report data types

export interface CategoryReportData {
  categoryId: number;
  categoryName: string;
  parentCategoryId?: number;
  parentCategoryName?: string;
  income: number;
  expense: number;
  balance: number;
  transactionCount: number;
  percentage?: number;
}

export interface MonthlyCompetenceData extends CategoryReportData {
  month: Date;
}

export interface CostCenterReportData {
  costCenterId: number;
  costCenterName: string;
  code?: string;
  parentCostCenterId?: number;
  income: number;
  expense: number;
  balance: number;
  budgetedAmount?: number;
  budgetVariance?: number;
  budgetVariancePercentage?: number;
  transactionCount: number;
}

export interface MonthlyCostCenterData extends CostCenterReportData {
  month: Date;
}

export interface PayablesReportData {
  transactionId: string;
  supplierId?: string;
  supplierName: string;
  amount: number;
  dueDate: Date;
  paymentDate?: Date;
  isPaid: boolean;
  isOverdue: boolean;
  daysOverdue?: number;
  agingBucket?: '0-30' | '31-60' | '61-90' | '90+';
  paymentMethod?: string;
  category?: string;
  costCenter?: string;
}

export interface SupplierSummaryData {
  supplierId: string;
  supplierName: string;
  totalAmount: number;
  totalTransactions: number;
  averageAmount: number;
  averagePaymentPeriod?: number;
  overdueAmount: number;
  overdueCount: number;
}

export interface ReceivablesReportData {
  transactionId: string;
  clientId?: string;
  clientName: string;
  clientContact?: string;
  clientEmail?: string;
  clientPhone?: string;
  description?: string;
  amount: number;
  dueDate: Date;
  paymentDate?: Date;
  isPaid: boolean;
  isOverdue: boolean;
  daysOverdue?: number;
  daysUntilDue?: number;
  agingBucket?: '0-30' | '31-60' | '61-90' | '90+';
  paymentMethod?: string;
  paymentAccountId?: number;
  paymentAccountName?: string;
  category?: string;
}

export interface ClientSummaryData {
  clientId: string;
  clientName: string;
  totalAmount: number;
  totalTransactions: number;
  averageAmount: number;
  averageReceiptPeriod?: number;
  overdueAmount: number;
  overdueCount: number;
  paymentReliabilityScore?: number;
}

export interface CashFlowReportData {
  date: Date;
  income: number;
  expense: number;
  balance: number;
  runningBalance: number;
  paymentAccountId?: number;
  paymentAccountName?: string;
}

export interface BankBalanceData {
  month: Date;
  paymentAccountId: number;
  paymentAccountName: string;
  openingBalance: number;
  closingBalance: number;
  totalInflow: number;
  totalOutflow: number;
  netFlow: number;
}

export interface DailyBalanceData {
  date: Date;
  accountId: string;
  accountName: string;
  openingBalance: number;
  inflows: number;
  outflows: number;
  closingBalance: number;
}

export interface MonthlyBalanceData {
  month: Date;
  accountId: string;
  accountName: string;
  openingBalance: number;
  inflows: number;
  outflows: number;
  closingBalance: number;
  growthPercentage: number;
}

export interface ProjectedBalanceData {
  date: Date;
  expectedInflows: number;
  expectedOutflows: number;
  projectedBalance: number;
  confidenceLevel: 'high' | 'medium' | 'low';
  scenario?: 'optimistic' | 'base' | 'pessimistic';
}

export interface AgingBucketData {
  bucket: '0-30' | '31-60' | '61-90' | '90+';
  amount: number;
  count: number;
  percentage: number;
}

export interface PaymentMethodData {
  paymentMethod: string;
  amount: number;
  count: number;
  percentage: number;
  averageDaysToPayment?: number;
}

export interface ClientDefaultRiskData {
  client: string;
  amount: number;
  daysOverdue: number;
  priority: 'low' | 'medium' | 'high' | 'critical';
}

export interface DefaultAnalysisData {
  agingBuckets: Array<{
    bucket: string;
    amount: number;
    count: number;
    percentage: number;
  }>;
  clientsRanking: ClientDefaultRiskData[];
  totalOverdue: number;
  totalClients: number;
  avgDaysOverdue: number;
}

/**
 * Client Receipts by Bank Data
 * Groups received amounts by client and payment account (bank)
 * Used for analyzing which banks/payment methods clients prefer for payments
 */
export interface ClientReceiptsByBankData {
  /** Client name (text field from transactions) */
  clientName: string;

  /** Payment account ID (foreign key to payment_accounts) */
  paymentAccountId: number;

  /** Payment account name (e.g., "Banco do Brasil - CC", "Nubank") */
  paymentAccountName: string;

  /** Bank name from payment_accounts table (optional) */
  bankName?: string;

  /** Total amount received in this payment account from this client */
  totalAmount: number;

  /** Number of transactions received in this payment account from this client */
  transactionCount: number;

  /** Average amount per transaction (totalAmount / transactionCount) */
  averageAmount: number;

  /** Percentage of total receipts from this client (optional) */
  percentage?: number;

  /** Date of first receipt in this payment account from this client (optional) */
  firstReceiptDate?: Date;

  /** Date of last receipt in this payment account from this client (optional) */
  lastReceiptDate?: Date;
}

// Export format types
export type ExportFormat = 'pdf' | 'excel' | 'csv';

export interface ExportOptions {
  format: ExportFormat;
  includeCharts?: boolean;
  includeSummary?: boolean;
  filename?: string;
}

// Cache configuration
export interface ReportCacheConfig {
  staleTime: number;
  cacheTime: number;
  enabled: boolean;
}

// Default cache configuration: 5-minute stale time, 15-minute cache time
export const DEFAULT_REPORT_CACHE: ReportCacheConfig = {
  staleTime: 5 * 60 * 1000, // 5 minutes
  cacheTime: 15 * 60 * 1000, // 15 minutes
  enabled: true,
};

// ============================================================================
// ENHANCED FILTER TYPES
// ============================================================================

/**
 * Enhanced report filters with additional options
 */
export interface EnhancedReportFilters extends ReportFilters {
  /** Minimum transaction amount */
  minAmount?: number;

  /** Maximum transaction amount */
  maxAmount?: number;

  /** Search text for description matching */
  searchText?: string;

  /** Include inactive entities */
  includeInactive?: boolean;

  /** Group by field for aggregation */
  groupBy?: string;

  /** Sort by field */
  sortBy?: string;

  /** Sort order */
  sortOrder?: SortOrder;
}

// ============================================================================
// ENHANCED REPORT DATA TYPES
// ============================================================================

/**
 * Enhanced report summary with additional metrics
 */
export interface EnhancedReportSummary extends ReportSummary {
  /** Average transaction amount */
  averageAmount: number;

  /** Number of unique entities */
  uniqueEntities: number;

  /** Period information */
  periodInfo: {
    start: Date;
    end: Date;
    days: number;
  };

  /** Largest income transaction */
  maxIncome?: number;

  /** Largest expense transaction */
  maxExpense?: number;
}

/**
 * Enhanced report metadata
 */
export interface EnhancedReportMetadata extends ReportMetadata {
  /** Report title */
  title: string;

  /** Report subtitle/description */
  subtitle?: string;

  /** Company information */
  company?: {
    name: string;
    document?: string;
    logo?: string;
  };

  /** Additional notes */
  notes?: string;

  /** Report version */
  version?: string;
}

// ============================================================================
// CATEGORY REPORT TYPES - 8 Report Types
// ============================================================================

/**
 * Category Summary Report
 * Overview of all categories with transaction counts and totals
 */
export interface CategorySummaryReportData {
  categories: Array<{
    category: Category;
    transactionCount: number;
    totalIncome: number;
    totalExpense: number;
    netAmount: number;
    percentageOfTotal: number;
    averageTransaction: number;
    lastTransactionDate?: Date;
  }>;
}

/**
 * Category Comparison Report
 * Period-over-period comparison of category performance
 */
export interface CategoryComparisonReportData {
  comparisons: Array<{
    category: Category;
    currentPeriod: {
      amount: number;
      transactionCount: number;
    };
    previousPeriod: {
      amount: number;
      transactionCount: number;
    };
    variance: {
      amount: number;
      percentage: number;
      trend: 'increase' | 'decrease' | 'stable';
    };
  }>;
}

/**
 * Category Trend Analysis
 * Time-series analysis of category performance
 */
export interface CategoryTrendReportData {
  categories: Array<{
    category: Category;
    timeSeries: Array<{
      period: Date;
      amount: number;
      transactionCount: number;
    }>;
    trend: {
      direction: 'upward' | 'downward' | 'stable';
      growthRate: number;
      volatility: number;
    };
  }>;
}

/**
 * Category Budget vs Actual
 * Comparison of budgeted amounts against actual spending
 */
export interface CategoryBudgetReportData {
  categories: Array<{
    category: Category;
    budget: number;
    actual: number;
    variance: number;
    variancePercentage: number;
    status: 'under' | 'on-target' | 'over';
    remainingBudget: number;
  }>;
}

/**
 * Top Categories by Amount
 * Ranked list of categories by transaction volume
 */
export interface TopCategoriesReportData {
  topIncome: Array<{
    category: Category;
    amount: number;
    transactionCount: number;
    percentageOfTotal: number;
  }>;
  topExpense: Array<{
    category: Category;
    amount: number;
    transactionCount: number;
    percentageOfTotal: number;
  }>;
}

/**
 * Category Distribution Report
 * Distribution analysis across income and expense categories
 */
export interface CategoryDistributionReportData {
  income: {
    categories: Array<{
      category: Category;
      amount: number;
      percentage: number;
    }>;
    total: number;
  };
  expense: {
    categories: Array<{
      category: Category;
      amount: number;
      percentage: number;
    }>;
    total: number;
  };
}

/**
 * Category Detail Report
 * Detailed transaction listing by category
 */
export interface CategoryDetailReportData {
  categories: Array<{
    category: Category;
    transactions: Transaction[];
    subtotal: number;
    transactionCount: number;
  }>;
}

/**
 * Category Utilization Report
 * Analysis of category usage patterns
 */
export interface CategoryUtilizationReportData {
  categories: Array<{
    category: Category;
    usageMetrics: {
      firstUsed?: Date;
      lastUsed?: Date;
      totalTransactions: number;
      averagePerMonth: number;
      daysActive: number;
    };
    utilizationRate: number;
    status: 'active' | 'underutilized' | 'inactive';
  }>;
}

// ============================================================================
// COST CENTER REPORT TYPES - 5 Report Types
// ============================================================================

/**
 * Cost Center Summary Report
 * Overview of all cost centers with transaction totals
 */
export interface CostCenterSummaryReportData {
  costCenters: Array<{
    costCenter: CostCenter;
    transactionCount: number;
    totalAmount: number;
    budgetedAmount?: number;
    variance?: number;
    percentageOfTotal: number;
  }>;
}

/**
 * Cost Center Hierarchy Report
 * Hierarchical view of cost centers with rollup totals
 */
export interface CostCenterHierarchyReportData {
  hierarchy: Array<{
    costCenter: CostCenter;
    level: number;
    children?: CostCenterHierarchyReportData['hierarchy'];
    directAmount: number;
    rollupAmount: number;
    transactionCount: number;
  }>;
}

/**
 * Cost Center Budget Analysis
 * Budget performance by cost center
 */
export interface CostCenterBudgetReportData {
  costCenters: Array<{
    costCenter: CostCenter;
    budget: number;
    actual: number;
    committed?: number;
    available: number;
    variance: number;
    variancePercentage: number;
    status: 'under' | 'on-target' | 'over' | 'critical';
  }>;
}

/**
 * Cost Center Detail Report
 * Detailed transaction breakdown by cost center
 */
export interface CostCenterDetailReportData {
  costCenters: Array<{
    costCenter: CostCenter;
    transactions: Transaction[];
    byCategory: Array<{
      category: Category;
      amount: number;
      transactionCount: number;
    }>;
    subtotal: number;
  }>;
}

/**
 * Cost Center Performance Report
 * Performance metrics and KPIs by cost center
 */
export interface CostCenterPerformanceReportData {
  costCenters: Array<{
    costCenter: CostCenter;
    performance: {
      totalSpent: number;
      budgetUtilization: number;
      averageTransactionSize: number;
      transactionFrequency: number;
    };
    trends: {
      monthOverMonth: number;
      yearOverYear: number;
    };
    ranking: number;
  }>;
}

// ============================================================================
// PAYABLES REPORT TYPES - 7 Report Types
// ============================================================================

/**
 * Accounts Payable Aging Report
 * Age analysis of outstanding payables
 */
export interface PayablesAgingReportData {
  ageGroups: Array<{
    range: string;
    transactions: Transaction[];
    totalAmount: number;
    count: number;
    percentageOfTotal: number;
  }>;
  summary: {
    current: number;
    days30: number;
    days60: number;
    days90: number;
    over90: number;
  };
}

/**
 * Payables by Supplier Report
 * Breakdown of payables by supplier
 */
export interface PayablesBySupplierReportData {
  suppliers: Array<{
    supplierName: string;
    transactions: Transaction[];
    totalAmount: number;
    paidAmount: number;
    pendingAmount: number;
    overdueAmount: number;
    transactionCount: number;
    averagePaymentTerm: number;
  }>;
}

/**
 * Payables Schedule Report
 * Payment schedule with due dates
 */
export interface PayablesScheduleReportData {
  schedule: Array<{
    dueDate: Date;
    transactions: Transaction[];
    totalAmount: number;
    status: 'upcoming' | 'due-today' | 'overdue';
  }>;
  upcomingTotal: number;
  overdueTotal: number;
}

/**
 * Overdue Payables Report
 * Analysis of overdue payments
 */
export interface OverduePayablesReportData {
  overdueTransactions: Array<{
    transaction: Transaction;
    daysOverdue: number;
    supplier?: string;
    category?: Category;
    amount: number;
  }>;
  summaryMetrics: {
    totalOverdue: number;
    averageDaysOverdue: number;
    oldestOverdue: number;
    affectedSuppliers: number;
  };
}

/**
 * Payables Payment History
 * Historical analysis of payment patterns
 */
export interface PayablesPaymentHistoryReportData {
  history: Array<{
    period: Date;
    totalPaid: number;
    transactionCount: number;
    averagePaymentTime: number;
    onTimePayments: number;
    latePayments: number;
  }>;
  metrics: {
    onTimePercentage: number;
    averagePaymentDelay: number;
  };
}

/**
 * Payables by Category Report
 * Payables analysis grouped by expense category
 */
export interface PayablesByCategoryReportData {
  categories: Array<{
    category: Category;
    transactions: Transaction[];
    totalAmount: number;
    paidAmount: number;
    pendingAmount: number;
    percentageOfTotal: number;
  }>;
}

/**
 * Supplier Performance Report
 * Analysis of supplier payment reliability
 */
export interface SupplierPerformanceReportData {
  suppliers: Array<{
    supplierName: string;
    metrics: {
      totalTransactions: number;
      totalAmount: number;
      averageAmount: number;
      onTimePayments: number;
      latePayments: number;
      averagePaymentDelay: number;
    };
    performanceScore: number;
    status: 'excellent' | 'good' | 'fair' | 'poor';
  }>;
}

// ============================================================================
// RECEIVABLES REPORT TYPES - 11 Report Types
// ============================================================================

/**
 * Default Analysis Report
 * Aging buckets and client ranking analysis for receivables default risk
 */
export interface DefaultAnalysisData {
  agingBuckets: Array<{
    bucket: string;
    amount: number;
    count: number;
    percentage: number;
  }>;
  clientsRanking: Array<{
    client: string;
    amount: number;
    daysOverdue: number;
    priority: 'low' | 'medium' | 'high' | 'critical';
  }>;
}

/**
 * Payment Method Distribution Report
 * Distribution of payment methods with trend analysis
 */
export interface PaymentMethodDistributionData {
  methods: Array<{
    method: string;
    amount: number;
    count: number;
    percentage: number;
    avgDays: number;
    color: string;
  }>;
  trend: Array<{
    month: string;
    [method: string]: number | string;
  }>;
}

/**
 * Accounts Receivable Aging Report
 * Age analysis of outstanding receivables
 */
export interface ReceivablesAgingReportData {
  ageGroups: Array<{
    range: string;
    transactions: Transaction[];
    totalAmount: number;
    count: number;
    percentageOfTotal: number;
  }>;
  summary: {
    current: number;
    days30: number;
    days60: number;
    days90: number;
    over90: number;
  };
}

/**
 * Receivables by Client Report
 * Breakdown of receivables by client
 */
export interface ReceivablesByClientReportData {
  clients: Array<{
    clientName: string;
    transactions: Transaction[];
    totalAmount: number;
    receivedAmount: number;
    pendingAmount: number;
    overdueAmount: number;
    transactionCount: number;
    averagePaymentTerm: number;
  }>;
}

/**
 * Collection Schedule Report
 * Expected collection schedule
 */
export interface CollectionScheduleReportData {
  schedule: Array<{
    dueDate: Date;
    transactions: Transaction[];
    totalAmount: number;
    status: 'upcoming' | 'due-today' | 'overdue';
  }>;
  expectedTotal: number;
  overdueTotal: number;
}

/**
 * Overdue Receivables Report
 * Analysis of overdue collections
 */
export interface OverdueReceivablesReportData {
  overdueTransactions: Array<{
    transaction: Transaction;
    daysOverdue: number;
    client?: string;
    category?: Category;
    amount: number;
    riskLevel: 'low' | 'medium' | 'high';
  }>;
  summaryMetrics: {
    totalOverdue: number;
    averageDaysOverdue: number;
    oldestOverdue: number;
    affectedClients: number;
  };
}

/**
 * Collection History Report
 * Historical collection performance
 */
export interface CollectionHistoryReportData {
  history: Array<{
    period: Date;
    totalCollected: number;
    transactionCount: number;
    averageCollectionTime: number;
    onTimeCollections: number;
    lateCollections: number;
  }>;
  metrics: {
    onTimePercentage: number;
    averageCollectionDelay: number;
  };
}

/**
 * Receivables by Category Report
 * Receivables analysis grouped by income category
 */
export interface ReceivablesByCategoryReportData {
  categories: Array<{
    category: Category;
    transactions: Transaction[];
    totalAmount: number;
    receivedAmount: number;
    pendingAmount: number;
    percentageOfTotal: number;
  }>;
}

/**
 * Client Payment Behavior Report
 * Analysis of client payment patterns
 */
export interface ClientPaymentBehaviorReportData {
  clients: Array<{
    clientName: string;
    metrics: {
      totalTransactions: number;
      totalAmount: number;
      averageAmount: number;
      onTimePayments: number;
      latePayments: number;
      averagePaymentDelay: number;
    };
    creditScore: number;
    riskLevel: 'low' | 'medium' | 'high';
  }>;
}

/**
 * Cash Collection Forecast
 * Projected cash collection timeline
 */
export interface CashCollectionForecastReportData {
  forecast: Array<{
    date: Date;
    expectedAmount: number;
    probability: number;
    transactions: Transaction[];
  }>;
  summaryProjections: {
    next30Days: number;
    next60Days: number;
    next90Days: number;
    beyond90Days: number;
  };
}

/**
 * DSO (Days Sales Outstanding) Report
 * Days Sales Outstanding analysis
 */
export interface DSOReportData {
  periods: Array<{
    period: Date;
    dso: number;
    averageReceivables: number;
    averageSales: number;
  }>;
  currentDSO: number;
  industryBenchmark?: number;
  trend: 'improving' | 'stable' | 'deteriorating';
}

/**
 * Bad Debt Analysis Report
 * Analysis of uncollectible receivables
 */
export interface BadDebtAnalysisReportData {
  badDebts: Array<{
    transaction: Transaction;
    client?: string;
    amount: number;
    daysOutstanding: number;
    writeOffDate?: Date;
    reason?: string;
  }>;
  summaryMetrics: {
    totalBadDebt: number;
    badDebtPercentage: number;
    affectedClients: number;
  };
}

/**
 * Revenue Recognition Report
 * Revenue recognition and realization analysis
 */
export interface RevenueRecognitionReportData {
  revenue: Array<{
    period: Date;
    billedRevenue: number;
    recognizedRevenue: number;
    deferredRevenue: number;
    realizedRevenue: number;
  }>;
  summaryMetrics: {
    totalBilled: number;
    totalRecognized: number;
    totalDeferred: number;
    realizationRate: number;
  };
}

// ============================================================================
// CASH FLOW REPORT TYPES - 9 Report Types
// ============================================================================

/**
 * Daily Cash Flow Report
 * Day-by-day cash flow analysis
 */
export interface DailyCashFlowReportData {
  dailyFlow: Array<{
    date: Date;
    openingBalance: number;
    inflows: number;
    outflows: number;
    netFlow: number;
    closingBalance: number;
    transactions: Transaction[];
  }>;
}

/**
 * Cash Flow Projection Report
 * Forward-looking cash flow forecast
 */
export interface CashFlowProjectionReportData {
  projections: Array<{
    date: Date;
    projectedInflows: number;
    projectedOutflows: number;
    projectedBalance: number;
    confidence: number;
    scenarios: {
      optimistic: number;
      realistic: number;
      pessimistic: number;
    };
  }>;
}

/**
 * Operating Cash Flow Report
 * Operating activities cash flow analysis
 */
export interface OperatingCashFlowReportData {
  periods: Array<{
    period: Date;
    revenue: number;
    operatingExpenses: number;
    operatingCashFlow: number;
    operatingMargin: number;
  }>;
  summaryMetrics: {
    totalRevenue: number;
    totalExpenses: number;
    netOperatingCashFlow: number;
    averageMargin: number;
  };
}

/**
 * Cash Flow by Account Report
 * Cash flow analysis by payment account
 */
export interface CashFlowByAccountReportData {
  accounts: Array<{
    account: PaymentAccount;
    openingBalance: number;
    inflows: number;
    outflows: number;
    netFlow: number;
    closingBalance: number;
    transactions: Transaction[];
  }>;
}

/**
 * Cash Flow Statement Report
 * Standard cash flow statement format
 */
export interface CashFlowStatementReportData {
  operatingActivities: {
    items: Array<{
      description: string;
      amount: number;
    }>;
    total: number;
  };
  investingActivities: {
    items: Array<{
      description: string;
      amount: number;
    }>;
    total: number;
  };
  financingActivities: {
    items: Array<{
      description: string;
      amount: number;
    }>;
    total: number;
  };
  netCashFlow: number;
  openingCash: number;
  closingCash: number;
}

/**
 * Cash Flow Variance Report
 * Actual vs projected cash flow variance analysis
 */
export interface CashFlowVarianceReportData {
  periods: Array<{
    period: Date;
    projectedInflows: number;
    actualInflows: number;
    inflowVariance: number;
    projectedOutflows: number;
    actualOutflows: number;
    outflowVariance: number;
    netVariance: number;
  }>;
}

/**
 * Liquidity Analysis Report
 * Liquidity and solvency analysis
 */
export interface LiquidityAnalysisReportData {
  metrics: {
    currentRatio: number;
    quickRatio: number;
    cashRatio: number;
    workingCapital: number;
    cashConversionCycle: number;
  };
  trends: Array<{
    period: Date;
    liquidity: number;
    status: 'healthy' | 'adequate' | 'concerning' | 'critical';
  }>;
}

/**
 * Cash Burn Rate Report
 * Cash consumption rate analysis
 */
export interface CashBurnRateReportData {
  periods: Array<{
    period: Date;
    cashBurned: number;
    runwayDays: number;
    burnRate: number;
  }>;
  currentBurnRate: number;
  currentRunway: number;
  projectedZeroDate?: Date;
}

/**
 * Cash Position Report
 * Current cash position and availability
 */
export interface CashPositionReportData {
  currentPosition: {
    totalCash: number;
    availableCash: number;
    restrictedCash: number;
    committedCash: number;
  };
  byAccount: Array<{
    account: PaymentAccount;
    balance: number;
    available: number;
    restricted: number;
  }>;
  projectedPosition: {
    next7Days: number;
    next30Days: number;
    next90Days: number;
  };
}

// ============================================================================
// REPORT GENERATION & EXPORT TYPES
// ============================================================================

/**
 * Report generation request parameters
 */
export interface ReportGenerationRequest {
  category: ReportCategory | ReportCategoryLegacy;
  reportType: ReportType;
  filters: ReportFilters | EnhancedReportFilters;
  format?: ReportFormat | ExportFormat;
  options?: {
    includeCharts?: boolean;
    includeDetails?: boolean;
    includeComparisons?: boolean;
    groupBy?: ReportPeriod | ReportPeriodLegacy;
    sortBy?: string;
    sortOrder?: SortOrder;
  };
}

/**
 * Report generation result
 */
export interface ReportGenerationResult<T = unknown> {
  success: boolean;
  report?: ReportData<T>;
  error?: string;
  warnings?: string[];
  generatedAt: Date;
  executionTime: number;
}

/**
 * Report export options
 */
export interface ReportExportOptions extends ExportOptions {
  pageSize?: 'A4' | 'Letter' | 'Legal';
  orientation?: 'portrait' | 'landscape';
  includeMetadata?: boolean;
  includeRawData?: boolean;
}

/**
 * Report export result
 */
export interface ReportExportResult {
  success: boolean;
  fileUrl?: string;
  fileName?: string;
  fileSize?: number;
  error?: string;
  exportedAt: Date;
}

// ============================================================================
// UTILITY TYPES
// ============================================================================

/**
 * Chart data structure for report visualizations
 */
export interface ReportChartData {
  type: 'line' | 'bar' | 'pie' | 'doughnut' | 'area';
  labels: string[];
  datasets: Array<{
    label: string;
    data: number[];
    backgroundColor?: string | string[];
    borderColor?: string | string[];
  }>;
  options?: Record<string, unknown>;
}

/**
 * Comparison period for period-over-period analysis
 */
export interface ComparisonPeriod {
  current: {
    start: Date;
    end: Date;
  };
  previous: {
    start: Date;
    end: Date;
  };
}

/**
 * Trend indicator
 */
export interface TrendIndicator {
  direction: 'up' | 'down' | 'stable';
  percentage: number;
  significance: 'strong' | 'moderate' | 'weak';
}

/**
 * Report pagination
 */
export interface ReportPagination {
  page: number;
  pageSize: number;
  totalPages: number;
  totalRecords: number;
}
